import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
  Box,
  Paper,
  Typography,
  Chip,
  CircularProgress,
  Alert,
  Button,
  Avatar,
  Divider,
  Tooltip,
  IconButton,
  Grid,
} from '@mui/material';
import { alpha } from '@mui/material/styles';
import {
  ArrowBack as ArrowBackIcon,
  Pets as PetsIcon,
  Person as PersonIcon,
  OpenInNew as OpenInNewIcon,
  LocationOn as LocationIcon,
  CalendarToday as CalendarIcon,
  Scale as ScaleIcon,
  Straighten as StraightenIcon,
  Bolt as BoltIcon,
  Transgender as GenderIcon,
} from '@mui/icons-material';
import { useApi } from '../contexts/ApiContext';
import { normalizeImageUrl } from '../services/imageUtils';

// Colour palette for energy chips — cycled by attribute ID so new values
// automatically get a colour without hardcoding any name strings.
const ENERGY_PALETTE = [
  { bg: '#D1FAE5', text: '#065F46', border: '#6EE7B7' },
  { bg: '#DBEAFE', text: '#1E40AF', border: '#93C5FD' },
  { bg: '#FEF3C7', text: '#92400E', border: '#FCD34D' },
  { bg: '#FEE2E2', text: '#991B1B', border: '#FCA5A5' },
];

function energyColors(energyAttr) {
  if (!energyAttr) return null;
  return ENERGY_PALETTE[energyAttr.id % ENERGY_PALETTE.length];
}

// Resolve display label from a DogAttributeDto (or null)
function attrLabel(attr) {
  if (!attr) return null;
  return attr.nameEn || attr.nameHe || attr.nameEs || `#${attr.id}`;
}

function StatCard({ label, value, icon: Icon, color }) {
  return (
    <Box
      sx={{
        p: 2,
        borderRadius: 2,
        border: '1px solid',
        borderColor: 'divider',
        bgcolor: 'background.paper',
        display: 'flex',
        flexDirection: 'column',
        gap: 0.5,
        minWidth: 0,
      }}
    >
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75 }}>
        {Icon && (
          <Avatar sx={{ width: 24, height: 24, bgcolor: alpha(color || '#6D28D9', 0.1), borderRadius: 1 }}>
            <Icon sx={{ fontSize: 13, color: color || '#6D28D9' }} />
          </Avatar>
        )}
        <Typography variant="caption" color="text.disabled" sx={{ fontWeight: 600, letterSpacing: '0.06em', textTransform: 'uppercase', fontSize: '0.62rem' }}>
          {label}
        </Typography>
      </Box>
      <Typography variant="body1" fontWeight={600} color={value ? 'text.primary' : 'text.disabled'} sx={{ fontSize: '0.95rem' }}>
        {value || '—'}
      </Typography>
    </Box>
  );
}

const DogDetails = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const api = useApi();

  const [dog, setDog] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [imgError, setImgError] = useState(false);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    api.dogs.getAdminById(id)
      .then((data) => { if (!cancelled) { setDog(data); setLoading(false); } })
      .catch(() => { if (!cancelled) { setError('Failed to load dog details.'); setLoading(false); } });
    return () => { cancelled = true; };
  }, [id, api]);

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', py: 8 }}>
        <CircularProgress />
      </Box>
    );
  }

  if (error || !dog) {
    return (
      <Box>
        <Button startIcon={<ArrowBackIcon />} onClick={() => navigate('/dogs')} sx={{ mb: 2 }}>
          Back to Dogs
        </Button>
        <Alert severity="error">{error || 'Dog not found.'}</Alert>
      </Box>
    );
  }

  const energyChipColors = energyColors(dog.energy);
  const imageUrl = !imgError && dog.image ? normalizeImageUrl(dog.image) : null;
  const createdDate = dog.createdAt
    ? new Date(dog.createdAt).toLocaleDateString(undefined, { year: 'numeric', month: 'long', day: 'numeric' })
    : null;

  return (
    <Box>
      {/* Back */}
      <Button
        startIcon={<ArrowBackIcon />}
        onClick={() => navigate('/dogs')}
        sx={{ mb: 3, color: 'text.secondary', fontWeight: 500 }}
      >
        Back to Dogs
      </Button>

      <Box
        sx={{
          display: 'grid',
          gridTemplateColumns: { xs: '1fr', md: '360px 1fr' },
          gap: 3,
          alignItems: 'start',
        }}
      >
        {/* ── Left: Image ── */}
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
          <Paper
            elevation={0}
            sx={{
              borderRadius: 3,
              overflow: 'hidden',
              border: '1px solid',
              borderColor: 'divider',
              aspectRatio: '1 / 1',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              bgcolor: alpha('#6D28D9', 0.04),
              position: 'relative',
            }}
          >
            {imageUrl ? (
              <Box
                component="img"
                src={imageUrl}
                alt={dog.name}
                onError={() => setImgError(true)}
                sx={{
                  width: '100%',
                  height: '100%',
                  objectFit: 'cover',
                  display: 'block',
                }}
              />
            ) : (
              <Box sx={{ textAlign: 'center' }}>
                <PetsIcon sx={{ fontSize: 80, color: alpha('#6D28D9', 0.2) }} />
                <Typography variant="caption" color="text.disabled" sx={{ display: 'block', mt: 1 }}>
                  No photo
                </Typography>
              </Box>
            )}
          </Paper>

          {/* Created date card */}
          {createdDate && (
            <Box
              sx={{
                display: 'flex',
                alignItems: 'center',
                gap: 1,
                px: 2,
                py: 1.5,
                borderRadius: 2,
                border: '1px solid',
                borderColor: 'divider',
                bgcolor: 'background.paper',
              }}
            >
              <CalendarIcon sx={{ fontSize: 16, color: 'text.disabled' }} />
              <Typography variant="body2" color="text.secondary">
                Added <strong>{createdDate}</strong>
              </Typography>
            </Box>
          )}
        </Box>

        {/* ── Right: Details ── */}
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2.5 }}>
          {/* Name + energy */}
          <Box>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, flexWrap: 'wrap', mb: 0.5 }}>
              <Typography variant="h4" fontWeight={700}>
                {dog.name}
              </Typography>
              {dog.energy && energyChipColors && (
                <Chip
                  label={attrLabel(dog.energy)}
                  size="small"
                  sx={{
                    bgcolor: energyChipColors.bg,
                    color: energyChipColors.text,
                    border: `1px solid ${energyChipColors.border}`,
                    fontWeight: 700,
                    fontSize: '0.75rem',
                    textTransform: 'capitalize',
                    height: 24,
                  }}
                />
              )}
            </Box>
            {dog.breed && (
              <Typography variant="subtitle1" color="text.secondary">
                {attrLabel(dog.breed)}
              </Typography>
            )}
          </Box>

          {/* Stat grid */}
          <Grid container spacing={1.5}>
            <Grid item xs={6} sm={4}>
              <StatCard label="Age" value={dog.age != null ? `${dog.age} year${dog.age !== 1 ? 's' : ''}` : null} icon={CalendarIcon} color="#6D28D9" />
            </Grid>
            <Grid item xs={6} sm={4}>
              <StatCard label="Gender" value={dog.gender ? dog.gender.charAt(0).toUpperCase() + dog.gender.slice(1) : null} icon={GenderIcon} color="#9333EA" />
            </Grid>
            <Grid item xs={6} sm={4}>
              <StatCard label="Size" value={attrLabel(dog.size)} icon={StraightenIcon} color="#3B82F6" />
            </Grid>
            <Grid item xs={6} sm={4}>
              <StatCard label="Weight" value={dog.weight != null ? `${dog.weight} kg` : null} icon={ScaleIcon} color="#10B981" />
            </Grid>
            <Grid item xs={6} sm={4}>
              <StatCard label="Energy" value={attrLabel(dog.energy)} icon={BoltIcon} color="#F59E0B" />
            </Grid>
            <Grid item xs={6} sm={4}>
              <StatCard label="Dog ID" value={`#${dog.id}`} icon={PetsIcon} color="#64748B" />
            </Grid>
          </Grid>

          {/* About */}
          {dog.about && (
            <Paper
              elevation={0}
              sx={{ p: 2.5, borderRadius: 2, border: '1px solid', borderColor: 'divider', bgcolor: 'background.paper' }}
            >
              <Typography variant="caption" color="text.disabled" sx={{ fontWeight: 600, letterSpacing: '0.06em', textTransform: 'uppercase', fontSize: '0.62rem', display: 'block', mb: 1 }}>
                About
              </Typography>
              <Typography variant="body2" color="text.secondary" sx={{ lineHeight: 1.7 }}>
                {dog.about}
              </Typography>
            </Paper>
          )}

          {/* Owner section */}
          <Paper
            elevation={0}
            sx={{ p: 2.5, borderRadius: 2, border: '1px solid', borderColor: 'divider', bgcolor: 'background.paper' }}
          >
            <Typography variant="caption" color="text.disabled" sx={{ fontWeight: 600, letterSpacing: '0.06em', textTransform: 'uppercase', fontSize: '0.62rem', display: 'block', mb: 1.5 }}>
              Owner
            </Typography>

            {dog.ownerUserId ? (
              <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: 1 }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                  <Avatar
                    sx={{
                      width: 38,
                      height: 38,
                      bgcolor: alpha('#6D28D9', 0.12),
                      color: '#6D28D9',
                      fontSize: '0.9rem',
                      fontWeight: 700,
                    }}
                  >
                    {(dog.ownerEmail || 'U').charAt(0).toUpperCase()}
                  </Avatar>
                  <Box>
                    <Typography variant="body2" fontWeight={600}>
                      {dog.ownerEmail || `User #${dog.ownerUserId}`}
                    </Typography>
                    {(dog.ownerCountry || dog.ownerCity) && (
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, mt: 0.25 }}>
                        <LocationIcon sx={{ fontSize: 13, color: 'text.disabled' }} />
                        <Typography variant="caption" color="text.secondary">
                          {[dog.ownerCity, dog.ownerCountry].filter(Boolean).join(', ')}
                        </Typography>
                      </Box>
                    )}
                  </Box>
                </Box>
                <Button
                  size="small"
                  variant="outlined"
                  endIcon={<OpenInNewIcon sx={{ fontSize: 14 }} />}
                  onClick={() => navigate(`/users/${dog.ownerUserId}`)}
                  sx={{ borderColor: 'divider', color: 'text.secondary', fontWeight: 500 }}
                >
                  View user
                </Button>
              </Box>
            ) : (
              <Typography variant="body2" color="text.disabled">No owner linked</Typography>
            )}
          </Paper>
        </Box>
      </Box>
    </Box>
  );
};

export default DogDetails;
